import { CheckCircle2, Clock3 } from 'lucide-react';
import {
  getBillingPlanChangeKind,
  useTranslation,
  type BillingPlanChangeKind,
  type BillingInterval,
  type BillingPlanCatalogItem,
  type BillingPlanKey,
  type TranslationKey,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  formatBillingPrice,
  formatBytes,
  intervalPriceKey,
  planNameKey,
} from '../lib/billing-formatters';

function actionKeyForChangeKind(changeKind: BillingPlanChangeKind): TranslationKey {
  const keys = {
    subscribe: 'admin.billing.actions.subscribe',
    current: 'admin.billing.currentPlan.title',
    upgrade: 'admin.billing.actions.upgrade',
    downgrade: 'admin.billing.actions.downgrade',
    interval_change: 'admin.billing.actions.intervalChange',
    continue_approval: 'admin.billing.actions.continueApproval',
  } satisfies Record<BillingPlanChangeKind, TranslationKey>;
  return keys[changeKind];
}

export function PlanCard(props: {
  readonly plan: BillingPlanCatalogItem;
  readonly currentPlanKey: BillingPlanKey | null;
  readonly currentInterval: BillingInterval | null;
  readonly pendingApprovalPlanKey: BillingPlanKey | null;
  readonly pendingApprovalInterval: BillingInterval | null;
  readonly disabled: boolean;
  readonly isPending: boolean;
  readonly onChoose: () => void;
}): React.JSX.Element {
  const { t, lang } = useTranslation();
  const changeKind = getBillingPlanChangeKind({
    currentPlanKey: props.currentPlanKey,
    currentInterval: props.currentInterval,
    targetPlanKey: props.plan.planKey,
    targetInterval: props.plan.interval,
    pendingApprovalPlanKey: props.pendingApprovalPlanKey,
    pendingApprovalInterval: props.pendingApprovalInterval,
  });
  const isCurrent = changeKind === 'current';
  const isPendingApproval = changeKind === 'continue_approval';
  const price = formatBillingPrice(props.plan.amountAgorot, lang);

  return (
    <article
      data-testid={`billing-plan-card-${props.plan.planKey}`}
      className={cn(
        'rounded-card border-sand-200 bg-paper flex h-full flex-col border p-5 text-start shadow-sm',
        isCurrent && 'border-zaatar shadow-card',
        isPendingApproval && 'border-saffron/60 shadow-card',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-sand-900 text-xl">
            {t(planNameKey(props.plan.planKey))}
          </h3>
          <p className="text-zaatar mt-2 text-lg font-semibold">
            {t(intervalPriceKey(props.plan.interval), { price })}
          </p>
        </div>
        {isCurrent ? <CheckCircle2 className="text-zaatar size-5" aria-hidden /> : null}
        {isPendingApproval ? <Clock3 className="text-saffron size-5" aria-hidden /> : null}
      </div>
      <ul className="text-sand-600 mt-5 space-y-2 text-sm leading-6">
        <li>{t('admin.billing.plan.activeProducts', { count: props.plan.activeProductLimit })}</li>
        <li>
          {t('admin.billing.plan.mediaStorage', {
            size: formatBytes(props.plan.mediaStorageBytes, lang),
          })}
        </li>
        <li>
          {t('admin.billing.plan.aiImageEdits', {
            count: props.plan.aiImageEditsPerPeriod,
          })}
        </li>
        <li>
          {t(
            props.plan.promotionToolsLevel === 'full'
              ? 'admin.billing.plan.promotionTools.full'
              : 'admin.billing.plan.promotionTools.basic',
          )}
        </li>
        <li>
          {t(
            props.plan.analyticsLevel === 'advanced'
              ? 'admin.billing.plan.analytics.advanced'
              : 'admin.billing.plan.analytics.basic',
          )}
        </li>
        <li>
          {t(
            props.plan.supportLevel === 'priority'
              ? 'admin.billing.plan.support.priority'
              : 'admin.billing.plan.support.standard',
          )}
        </li>
      </ul>
      <Button
        type="button"
        className="mt-5 w-full"
        variant={isCurrent ? 'outline' : 'default'}
        disabled={props.disabled || props.isPending || isCurrent}
        onClick={props.onChoose}
      >
        {isCurrent
          ? t('admin.billing.currentPlan.title')
          : isPendingApproval
            ? t('admin.billing.actions.continueApproval')
            : t(actionKeyForChangeKind(changeKind))}
      </Button>
    </article>
  );
}
